import { useEffect, useState, useRef } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import { Html } from '@react-three/drei'
import { Perf } from 'r3f-perf'

// GPU Stats monitoring component using r3f-perf
export function GPUStatsMonitor({ enabled = false }) {
    // Don't render if not enabled
    if (!enabled) return null
    
    return (
        <Perf 
            position="top-left"
            showGraph={true}
            deepAnalyze={true}
            colorBlind={false}
            antialias={false}
            overClock={true}
            matrixUpdate={true}
            minimal={false}
            customData={{
                value: 0,
                name: 'Custom',
                info: ''
            }}
        />
    )
}

// Performance monitoring component for testing
export function PerformanceMonitor({ enabled = false }) {
    const [stats, setStats] = useState({
        fps: 0,
        frameTime: 0,
        renderCount: 0,
        lastRenderTime: 0
    })
    
    const frameCount = useRef(0)
    const lastTime = useRef(performance.now())
    const startTime = useRef(performance.now())
    
    useFrame(() => {
        if (!enabled) return
        
        frameCount.current++
        const now = performance.now()
        const delta = now - lastTime.current
        
        // Update stats every second
        if (delta >= 1000) {
            const fps = Math.round((frameCount.current * 1000) / delta)
            const avgFrameTime = delta / frameCount.current
            const totalTime = now - startTime.current
            
            setStats({
                fps,
                frameTime: Math.round(avgFrameTime * 100) / 100,
                renderCount: frameCount.current,
                lastRenderTime: Math.round(totalTime / 1000)
            })
            
            frameCount.current = 0
            lastTime.current = now
        }
    })
    
    if (!enabled) return null
    
    return (
        <div style={{
            position: 'fixed',
            top: '10px',
            right: '10px',
            background: 'rgba(0,0,0,0.8)',
            color: 'white',
            padding: '10px',
            fontFamily: 'monospace',
            fontSize: '12px',
            borderRadius: '5px',
            zIndex: 1000
        }}>
            <div>FPS: {stats.fps}</div>
            <div>Frame Time: {stats.frameTime}ms</div>
            <div>Renders: {stats.renderCount}</div>
            <div>Runtime: {stats.lastRenderTime}s</div>
        </div>
    )
}

// Battery monitoring hook for mobile devices
export function useBatteryMonitor() {
    const [batteryInfo, setBatteryInfo] = useState(null)
    
    useEffect(() => {
        async function getBattery() {
            if ('getBattery' in navigator) {
                try {
                    const battery = await navigator.getBattery()
                    
                    const updateBatteryInfo = () => {
                        setBatteryInfo({
                            level: Math.round(battery.level * 100),
                            charging: battery.charging,
                            chargingTime: battery.chargingTime,
                            dischargingTime: battery.dischargingTime
                        })
                    }
                    
                    updateBatteryInfo()
                    
                    battery.addEventListener('levelchange', updateBatteryInfo)
                    battery.addEventListener('chargingchange', updateBatteryInfo)
                    
                    return () => {
                        battery.removeEventListener('levelchange', updateBatteryInfo)
                        battery.removeEventListener('chargingchange', updateBatteryInfo)
                    }
                } catch (error) {
                    console.log('Battery API not supported')
                }
            }
        }
        
        getBattery()
    }, [])
    
    return batteryInfo
}

// Component to display battery info
export function BatteryMonitor({ enabled = false }) {
    const batteryInfo = useBatteryMonitor()
    
    if (!enabled || !batteryInfo) return null
    
    return (
        <div style={{
            position: 'fixed',
            top: '10px',
            left: '10px',
            background: 'rgba(0,0,0,0.8)',
            color: 'white',
            padding: '10px',
            fontFamily: 'monospace',
            fontSize: '12px',
            borderRadius: '5px',
            zIndex: 1000
        }}>
            <div>Battery: {batteryInfo.level}%</div>
            <div>Status: {batteryInfo.charging ? 'Charging' : 'Discharging'}</div>
            {!batteryInfo.charging && batteryInfo.dischargingTime !== Infinity && (
                <div>Time Left: {Math.round(batteryInfo.dischargingTime / 3600)}h</div>
            )}
        </div>
    )
}